import React from 'react';
import { withRouter } from 'react-router-dom';
import _startCase from 'lodash/startCase';
import { defineMessages, useIntl } from '@kyruus/intl';
import { visuallyHidden } from '@biome-design-system/styles';
import { Box } from '@biome-design-system/ui/Box';
import { Typography } from '@biome-design-system/ui/Typography';
import _kebabCase from 'lodash/kebabCase';
import { LocationFilter } from '../../Filters/LocationFilter';
import { TypeaheadFilter } from '../../Filters/TypeaheadFilter';
import { BasicFacet } from './basic-facet';
import { ClearFilter } from './clear-filter';
import { FacetFieldSet, StyledLegend } from '../../styles';

const messages = defineMessages({
  clear: {
    id: 'facet.clearall',
    description: 'Text to clear all applied filters on the facet',
    defaultMessage: 'Clear'
  },
  clearallAriaLabel: {
    id: 'facet.clearallAriaLabel',
    description: 'Text to clear all applied filters on the facet',
    defaultMessage: 'Clear All {facetName} filters'
  },
  searchFilterFormDescription: {
    id: 'facet.form.description',
    description: 'Accessibility description for the search filter form.',
    defaultMessage:
      'Changing an input in this form will immediately trigger a new search and subsequent change of context.'
  }
});

export const FacetContainer = withRouter(
  ({
    searchSummary,
    config,
    customerConfig,
    facetName,
    getUpdatedSearch,
    history,
    log
  }) => {
    const intl = useIntl();
    const updateSearch = (modifications) => {
      history.push(getUpdatedSearch(modifications));
    };

    const searchableLocation =
      config.allow_location_change !== false || !searchSummary.location;

    const facetLabelMessageDescriptor = {
      id: `field.name.${config.field}`,
      defaultMessage: _startCase(config.field),
      description: 'The name displayed above the facet'
    };

    const facetKey = config.field;
    const labelId = `${_kebabCase(facetKey)}-label`;
    const customerConfigFacetConfig = (customerConfig?.facets_v9 || []).find(
      (facetConfig) => facetConfig.field === facetKey
    );
    const facetIsComposite =
      !!customerConfigFacetConfig && !!customerConfigFacetConfig?.composite;

    let facet = {};
    switch (config.type) {
      case 'location':
        facet = (
          <Box
            sx={{ clear: 'both' }}
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Box p={2}>
              <LocationFilter
                locationFacet={config}
                searchSummary={searchSummary}
                updateSearch={updateSearch}
                log={log}
              />
            </Box>
            <ClearFilter
              config={config}
              facetName={facetName}
              searchableLocation={searchableLocation}
              log={log}
              getUpdatedSearch={getUpdatedSearch}
              labelName={facetLabelMessageDescriptor}
            />
          </Box>
        );
        break;
      case 'typeahead':
        facet = (
          <Box
            sx={{ clear: 'both' }}
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Box p={2}>
              <TypeaheadFilter
                facet={config}
                labelId={labelId}
                hideCount={facetIsComposite}
                updateSearch={updateSearch}
              />
            </Box>
            <ClearFilter
              config={config}
              facetName={facetName}
              searchableLocation={searchableLocation}
              log={log}
              getUpdatedSearch={getUpdatedSearch}
              labelName={facetLabelMessageDescriptor}
            />
          </Box>
        );
        break;
      default:
        facet = (
          <BasicFacet
            config={config}
            facetName={facetName}
            hideCount={facetIsComposite}
            log={log}
            labelName={facetLabelMessageDescriptor}
            searchableLocation={searchableLocation}
            getUpdatedSearch={getUpdatedSearch}
            updateSearch={updateSearch}
          />
        );
    }

    return (
      <FacetFieldSet className="filter-group">
        <StyledLegend id={labelId}>
          <Typography sx={{ fontWeight: 'bold' }} variant="h5" component="span">
            {intl.formatMessage(facetLabelMessageDescriptor)}
          </Typography>
        </StyledLegend>
        {facet}
      </FacetFieldSet>
    );
  }
);

FacetContainer.displayName = 'FacetContainer';

export const FacetList = ({
  searchSummary,
  config,
  facets,
  getUpdatedSearch,
  distanceSort,
  log,
  showLocationFacet
}) => {
  const intl = useIntl();
  const facetList = facets.map((facetConfig) => {
    if (facetConfig.field === 'location' && !showLocationFacet) return null;
    if (
      [
        'locations.primary_marketable_location_id',
        'locations.associated_marketable_location_ids',
        'provider.id'
      ].includes(facetConfig.field)
    ) {
      return null;
    }

    return (facetConfig.field === 'location' &&
      facetConfig.distance_options.length) ||
      (facetConfig.terms && facetConfig.terms.length) ? (
      <FacetContainer
        searchSummary={searchSummary}
        key={facetConfig.field}
        facetName={facetConfig.field}
        customerConfig={config}
        config={facetConfig}
        distanceSort={distanceSort}
        getUpdatedSearch={getUpdatedSearch}
        log={log}
      />
    ) : null;
  });

  return (
    <form
      id="searchFilters"
      name="searchFilters"
      onSubmit={(e) => e.preventDefault()}
      aria-describedby="searchFilterFormDescription"
    >
      <Box id="searchFilterFormDescription" sx={visuallyHidden}>
        {intl.formatMessage(messages.searchFilterFormDescription)}
      </Box>
      {facetList}
    </form>
  );
};

FacetList.displayName = 'FacetList';
